import chartTheme from "@components/Charts/chartTheme";
import TitledChartSkeleton from "@components/TitledChartSkeleton";
import { ClientContext } from "@context/Client/ClientContext";
import { LineChart } from "@dive.tech/ui-graphs";
import LineChartTooltip from "@dive.tech/ui-graphs/Tooltips/LineChartTooltip";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useModelScenario } from "@services/api";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface KpiScenarioLineChartProps {
  scenario1Id: string;
  scenario2Id: string;
}

const KpiScenarioLineChart = ({
  scenario1Id,
  scenario2Id
}: KpiScenarioLineChartProps) => {
  const { t } = useTranslation();
  const { currentClient } = useContext(ClientContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Ref and state for dynamic width
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number>(1000);

  const { data: scenario1, isFetching: isLoadingScenario1 } = useModelScenario(
    scenario1Id ?? ""
  );
  const { data: scenario2, isFetching: isLoadingScenario2 } = useModelScenario(
    scenario2Id ?? ""
  );

  const isLoading = useMemo(
    () => isLoadingScenario1 || isLoadingScenario2,
    [isLoadingScenario1, isLoadingScenario2]
  );

  const scenario1Name = useMemo(() => {
    return scenario1?.scenario.name || "Scenario 1";
  }, [scenario1]);

  const scenario2Name = useMemo(() => {
    return scenario2?.scenario.name || "Scenario 2";
  }, [scenario2]);

  const chartData = useMemo(() => {
    return {
      data: [
        {
          id: scenario1Name,
          color: "#6172F3",
          data: (scenario1?.outputs || []).map((output) => ({
            x: output.time_period,
            y: output.value
          }))
        },
        {
          id: scenario2Name,
          color: "#85E139",
          data: (scenario2?.outputs || []).map((output) => ({
            x: output.time_period,
            y: output.value
          }))
        }
      ],
      max:
        Math.max(
          ...(scenario1?.outputs || []).map((output) => output.value),
          ...(scenario2?.outputs || []).map((output) => output.value)
        ) * 1.05
    };
  }, [scenario1, scenario2, scenario1Name, scenario2Name]);

  // Measure container width on mount and resize
  useEffect(() => {
    const updateWidth = () => {
      if (chartContainerRef.current) {
        const width = chartContainerRef.current.offsetWidth;
        setChartWidth(width > 0 ? width : 1000);
      }
    };

    updateWidth();

    // Use ResizeObserver for better performance
    const resizeObserver = new ResizeObserver(updateWidth);
    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <React.Fragment>
      {isLoading ? (
        <TitledChartSkeleton />
      ) : (
        <Box className="w-full">
          <Typography variant="h6" component="div" className="pt-4 pl-6">
            {t("simulator.benchmark.kpi_per_scenario", {
              kpi: currentClient.client.kpi_label
            })}
          </Typography>
          <Box sx={{ height: "500px" }} ref={chartContainerRef}>
            {chartData && (
              <LineChart
                data={chartData.data}
                responsiveLineProps={{
                  tooltip: (props: any) =>
                    LineChartTooltip({
                      point: props.point,
                      xAxis: { xType: "string", xDecimals: 0 },
                      yAxis: { yType: "number", yDecimals: 0 }
                    }),
                  theme: chartTheme,
                  xScale: undefined,
                  enableGridX: true,
                  enableGridY: true,
                  axisLeft: {
                    legend: currentClient.client.kpi_label,
                    //format: (value) => `${Number(value) / 1_000}k`,
                    tickSize: 10,
                    legendOffset: -70,
                    legendPosition: "middle"
                  },
                  curve: "linear",
                  axisBottom: {
                    tickRotation: isMobile ? -25 : 0,
                    tickPadding: 20,
                    legendPosition: "middle",
                    legendOffset: 80,
                    legend: t("simulator.benchmark.weeks")
                  },
                  yScale: {
                    type: "linear",
                    min: "auto",
                    max: chartData.max
                  },
                  margin: { top: 50, left: 90, right: 30, bottom: 120 },
                  width: chartWidth,
                  height: 500,
                  legends: [
                    {
                      anchor: "top",
                      direction: "column",
                      itemHeight: 10,
                      itemWidth: 150,
                      itemDirection: "left-to-right",
                      symbolSize: 15,
                      itemsSpacing: isMobile ? 10 : 10,
                      toggleSerie: true,
                      translateX: isMobile ? 50 : 0,
                      translateY: isMobile ? -30 : -20,
                      itemOpacity: 1,
                      symbolShape: (s) => (
                        <line
                          x1={s.x}
                          y1={s.y + 7} // Coloca la línea a distancia del y inicial para centrarla verticalmente
                          x2={s.x + 15} // Largo de la línea
                          y2={s.y + 7} // Mantener y1 e y2 iguales para que sea horizontal
                          stroke={s.fill} // El color de la línea es el mismo que el 'fill'
                          strokeWidth={4} // Grosor de la línea
                          strokeLinecap="round" // Hace los extremos de la línea redondeados
                        />
                      )
                    }
                  ]
                }}
              />
            )}
          </Box>
        </Box>
      )}
    </React.Fragment>
  );
};

export default KpiScenarioLineChart;
